import React from 'react';
import { Pill } from '../../components/ui';
import type { AdaptedReport } from '../../components/titleAnalysis/reportAdapter';
import { displayList } from '../../components/titleAnalysis/displayUtils/displayList';
import { displayValue } from '../../components/titleAnalysis/displayUtils/displayValue';
import { formatConfidence } from '../../components/titleAnalysis/displayUtils/labelFormatters';
import {
  confidenceTone,
  encumbranceStatusMeta,
  encumbranceTypeLabel,
  formatMoney,
  linkMeta,
  sortEncumbrances,
  sortVestingChain,
} from './reportModel';
import { Field, TabEmpty, TableShell, Td, Th } from './reportParts';

/**
 * Vesting chain, Encumbrances, Parties and Commitment tab bodies.
 *
 * Requirement 11.8; the subject mortgage row in Encumbrances is tinted.
 */

export const VestingChainTab: React.FC<{ vestingChain: AdaptedReport['vestingChain'] }> = ({
  vestingChain,
}) => {
  if (vestingChain.length === 0) {
    return <TabEmpty>No vesting chain information available.</TabEmpty>;
  }
  return (
    <TableShell label="Vesting chain">
      <thead>
        <tr>
          <Th>#</Th>
          <Th>Grantor</Th>
          <Th>Grantee</Th>
          <Th>Type</Th>
          <Th>Executed</Th>
          <Th>Recorded</Th>
          <Th>Consideration</Th>
          <Th>Instrument</Th>
          <Th>Link to prior</Th>
        </tr>
      </thead>
      <tbody>
        {sortVestingChain(vestingChain).map((link) => {
          const meta = linkMeta(link.linkToPrior);
          return (
            <tr key={link.sequence} data-testid="vesting-row">
              <Td className="font-bold text-gray-900">{link.sequence}</Td>
              <Td>{displayList(link.grantors)}</Td>
              <Td className="font-bold text-gray-900">{displayList(link.grantees)}</Td>
              <Td>{displayValue(link.conveyanceType)}</Td>
              <Td>{displayValue(link.executedDate)}</Td>
              <Td>{displayValue(link.recordedDate)}</Td>
              <Td>{formatMoney(link.consideration)}</Td>
              <Td mono>{displayValue(link.instrumentId)}</Td>
              <Td>
                <Pill tone={meta.tone}>{meta.label}</Pill>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </TableShell>
  );
};

export const EncumbrancesTab: React.FC<{ encumbrances: AdaptedReport['encumbrances'] }> = ({
  encumbrances,
}) => {
  if (encumbrances.length === 0) {
    return <TabEmpty>No encumbrances found.</TabEmpty>;
  }
  return (
    <TableShell label="Encumbrances">
      <thead>
        <tr>
          <Th>Priority</Th>
          <Th>Type</Th>
          <Th>Instrument</Th>
          <Th>Holder of record</Th>
          <Th>Debtors</Th>
          <Th>Amount</Th>
          <Th>Recorded</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {sortEncumbrances(encumbrances).map((enc) => {
          const status = encumbranceStatusMeta(enc.status);
          return (
            <tr
              key={enc.encumbranceId}
              data-testid="encumbrance-row"
              data-subject={enc.isSubjectMortgage ? 'true' : 'false'}
              className={enc.isSubjectMortgage ? 'bg-[#F5F3FF]' : undefined}
            >
              <Td className="font-bold text-gray-900">{enc.derivedPriorityRank}</Td>
              <Td>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-bold text-gray-900">
                    {encumbranceTypeLabel(enc.type)}
                  </span>
                  {enc.isSubjectMortgage ? <Pill tone="selected">Subject</Pill> : null}
                </div>
                <div className="font-mono text-xs text-gray-500">{enc.encumbranceId}</div>
              </Td>
              <Td mono>{displayValue(enc.instrumentId)}</Td>
              <Td>
                <div>{displayValue(enc.currentHolderOfRecord)}</div>
                {enc.originalHolder && enc.originalHolder !== enc.currentHolderOfRecord ? (
                  <div className="text-xs text-gray-500">Originally {enc.originalHolder}</div>
                ) : null}
                {enc.assignmentChain.map((a, idx) => (
                  <div key={idx} className="text-xs text-gray-500">
                    {a.assignor} → {a.assignee}
                    {a.recordedDate ? ` (${a.recordedDate})` : ''}
                    {!a.linkValid ? <span className="text-[#991b1b]"> · Broken link</span> : null}
                  </div>
                ))}
              </Td>
              <Td>{displayList(enc.debtors)}</Td>
              <Td>{formatMoney(enc.amount)}</Td>
              <Td>{displayValue(enc.recordedDate)}</Td>
              <Td>
                <Pill tone={status.tone} title={enc.statusBasis || undefined}>
                  {status.label}
                </Pill>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </TableShell>
  );
};

export const PartiesTab: React.FC<{ entities: AdaptedReport['entities'] }> = ({ entities }) => {
  if (entities.length === 0) {
    return <TabEmpty>No entity resolution information available.</TabEmpty>;
  }
  return (
    <TableShell label="Parties">
      <thead>
        <tr>
          <Th>Party</Th>
          <Th>Name variants</Th>
          <Th>Confidence</Th>
          <Th>Basis</Th>
        </tr>
      </thead>
      <tbody>
        {entities.map((entity) => (
          <tr key={entity.canonicalPartyId} data-testid="party-row">
            <Td>
              <div className="font-bold text-gray-900">{displayValue(entity.canonicalName)}</div>
              <div className="font-mono text-xs text-gray-500">{entity.canonicalPartyId}</div>
            </Td>
            <Td>{displayList(entity.variants)}</Td>
            <Td>
              <Pill tone={confidenceTone(entity.confidence)}>
                {formatConfidence(entity.confidence)}
              </Pill>
            </Td>
            <Td>{displayValue(entity.basis)}</Td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
};

function matchPill(value: boolean | null): React.ReactNode {
  if (value === true) return <Pill tone="success">Matches</Pill>;
  if (value === false) return <Pill tone="danger">Does not match</Pill>;
  return <Pill tone="neutral">Undetermined</Pill>;
}

export const CommitmentTab: React.FC<{ commitment: AdaptedReport['commitment'] }> = ({
  commitment,
}) => {
  if (!commitment.provided) {
    return <TabEmpty>No title commitment was provided.</TabEmpty>;
  }
  return (
    <div data-testid="commitment-tab" className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Vesting matches">{matchPill(commitment.vestingMatches)}</Field>
      <Field label="Insured matches subject loan">
        {matchPill(commitment.insuredMatchesSubjectLoan)}
      </Field>
      <Field label="Exceptions missing from Schedule B">
        {displayList(commitment.exceptionsMissingFromScheduleB)}
      </Field>
      <Field label="Schedule B items not found in search">
        {displayList(commitment.scheduleBItemsNotFoundInSearch)}
      </Field>
      <Field label="Notes" className="md:col-span-2">
        {displayValue(commitment.notes)}
      </Field>
    </div>
  );
};
